import { useCallback, useEffect, useRef } from "react";
import $ from "jquery";
import * as momentModule from "moment";
import { RiCalendarLine, RiCloseLine } from "react-icons/ri";
import { DISPLAY_DATE_FORMAT_PICKER_MOMENT } from "../../utils/dateUtils";

window.$ = window.jQuery = $;
const moment = momentModule.default || momentModule;
window.moment = moment;

import "bootstrap-daterangepicker/daterangepicker.css";
import "./daterange.css";

const scheduleAnimationFrame =
  typeof globalThis !== "undefined" && typeof globalThis.requestAnimationFrame === "function"
    ? (cb) => globalThis.requestAnimationFrame(cb)
    : (cb) => globalThis.setTimeout(cb, 0);

/**
 * Single calendar date (MMM D, YYYY display) — same daterangepicker stack as {@link DateRangePicker}.
 * @param {{ date: string } | null} value - `date` is YYYY-MM-DD, or null when cleared
 */
const SingleDatePicker = ({
  value,
  onChange,
  placeholder = "Select date",
  minDate,
  maxDate,
  className = "",
  showIcon = true,
  disabled = false,
  /** When true, shows a clear control to remove the selected date (optional fields). */
  clearable = false,
}) => {
  const inputRef = useRef(null);
  /** Latest props — async plugin init reads stale closures; refs stay current. */
  const valueRef = useRef(value);
  const minDateRef = useRef(minDate);
  const maxDateRef = useRef(maxDate);
  valueRef.current = value;
  minDateRef.current = minDate;
  maxDateRef.current = maxDate;

  const applyValueToPicker = useCallback(($input, drp) => {
    const v = valueRef.current;
    if (v?.date) {
      const m = moment(v.date, "YYYY-MM-DD");
      drp.setStartDate(m);
      drp.setEndDate(m);
      $input.val(m.format(DISPLAY_DATE_FORMAT_PICKER_MOMENT));
    } else {
      $input.val("");
    }
  }, []);

  /* Bootstrap daterangepicker: init once; sync from `value` in the following effect. */
  useEffect(() => {
    let isMounted = true;
    const el = inputRef.current;

    const setup = async () => {
      await import("bootstrap-daterangepicker");
      if (!isMounted || !inputRef.current) return;

      const $input = $(inputRef.current);
      const v = valueRef.current;
      const start = v?.date ? moment(v.date, "YYYY-MM-DD") : moment();

      $input.daterangepicker({
        singleDatePicker: true,
        autoApply: true,
        showDropdowns: true,
        drops: "down",
        opens: "center",
        startDate: start,
        minDate: minDateRef.current ? moment(minDateRef.current) : undefined,
        maxDate: maxDateRef.current ? moment(maxDateRef.current) : undefined,
        locale: {
          format: DISPLAY_DATE_FORMAT_PICKER_MOMENT,
        },
        parentEl: "body",
      });

      const drp = $input.data("daterangepicker");
      if (drp) {
        applyValueToPicker($input, drp);
      }

      $input.on("apply.daterangepicker", (_ev, picker) => {
        onChange?.({ date: picker.startDate.format("YYYY-MM-DD") });
      });

      $input.on("cancel.daterangepicker", () => {
        $input.val("");
        onChange?.(null);
      });
    };

    setup();

    return () => {
      isMounted = false;
      if (el) $(el).data("daterangepicker")?.remove();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- plugin binds once; sync via effect below
  }, [applyValueToPicker]);

  useEffect(() => {
    let cancelled = false;
    let frames = 0;
    const maxFrames = 90;

    const tick = () => {
      if (cancelled) return;
      const el = inputRef.current;
      if (!el) return;
      const $input = $(el);
      const drp = $input.data("daterangepicker");
      if (!drp) {
        frames += 1;
        if (frames < maxFrames) scheduleAnimationFrame(tick);
        return;
      }
      applyValueToPicker($input, drp);
    };

    tick();
    return () => {
      cancelled = true;
    };
  }, [value?.date, minDate, maxDate, applyValueToPicker]);

  const hasValue = Boolean(value?.date);

  const handleClear = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onChange?.(null);
    const el = inputRef.current;
    if (el) $(el).val("");
  };

  const inputPaddingRight = clearable && hasValue && !disabled ? "pr-9" : "pr-3";

  return (
    <div className="relative w-full flex items-center">
      {showIcon && (
        <RiCalendarLine
          size={18}
          className="text-gray-500 absolute left-3 pointer-events-none z-10"
          aria-hidden
        />
      )}
      <input
        ref={inputRef}
        type="text"
        readOnly
        disabled={disabled}
        placeholder={placeholder}
        className={`w-full py-2 text-sm border border-gray-300 rounded-md h-10
          focus:outline-none focus:ring-2 focus:ring-blue-500
          bg-white text-text-primary ${showIcon ? `pl-10 ${inputPaddingRight}` : `pl-3 ${inputPaddingRight}`}
          ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"} ${className}`}
      />
      {clearable && hasValue && !disabled ? (
        <button
          type="button"
          onClick={handleClear}
          className="absolute right-2 z-20 flex h-6 w-6 items-center justify-center rounded text-gray-500 hover:bg-gray-100 hover:text-gray-800"
          aria-label="Clear date"
          title="Clear date"
        >
          <RiCloseLine size={16} aria-hidden />
        </button>
      ) : null}
    </div>
  );
};

export default SingleDatePicker;
